import React, { useEffect, useState } from "react";
import { Typography, Pagination, Spin, message, TableColumnsType, Dropdown, Menu, Space } from "antd";
import { Icon } from "@iconify/react";
import { useNavigate } from "react-router-dom";
import { debounce } from "lodash";
import { DataTable } from "../../../../../../Components/DataTable/DataTable";
import { panelApiUrl } from "~/Utils";
import { api } from "../../../../../../../SPA/Services/api.service";
import { getFormattedDate } from "@utils/CommonUtils";
import { updatedAtOf, updatedByOf } from "../../../../../../Utils/auditFields";
import styles from "@styles/variables.module.scss";

export const TemplateList = () => {
  const navigate = useNavigate();
  const [templateData, setTemplateData] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [page, setPage] = useState<number>(0);
  const [searchString, setSearchString] = useState<string>("");
  const recordsPerPage = 10;
  const [clearSearch, setClearSearch] = useState<boolean>(false);

  useEffect(() => {
    fetchTemplateData(searchString, page);
  }, [page]);

  useEffect(() => {
    if (clearSearch) {
      const timer = setTimeout(() => setClearSearch(false), 100);
      return () => clearTimeout(timer);
    }
  }, [clearSearch]);

  const fetchTemplateData = (searchText: string = "", currentPage: number = 0) => {
    setLoading(true);
    setError(null);
    const url = `${panelApiUrl}/template?page=${currentPage}&size=${recordsPerPage}&searchText=${encodeURIComponent(
      searchText
    )}`;

    api
      .get(url)
      .then((res: any) => {
        const responseData = res?.data?.response?.data;
        const records = responseData?.records || [];
        setTotalRecords(responseData?.totalCount || 0);

        const mappedData = records.map((item: any) => ({
          key: item.id,
          id: item.id,
          templateName: item.templateName || item.itemName || "-",
          createdAt: getFormattedDate(item.createdOn) || "-",
          createdBy: item.createdBy || "-",
          modifiedAt: updatedAtOf(item),
          modifiedBy: updatedByOf(item),
          deleted: item.deleted,
          ...item,
        }));
        setTemplateData(mappedData);
      })
      .catch((err: any) => {
        console.error("Error fetching Template data:", err);
        setTemplateData([]);
        setTotalRecords(0);
        setError("Failed to fetch Template data");
        message.error("Error fetching Template data");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleSearchChange = debounce((value: string, isLoading: boolean) => {
    setClearSearch(false);
    setSearchString(value);
    setPage(0);
    setLoading(isLoading);
    fetchTemplateData(value, 0);
  }, 1000);

  const menuClick = (key: string, record: any) => {
    if (key === "edit") {
      navigate(`/workorder-manage-catalog/template-edit?id=${record?.id}`);
    }
  };

  const templateColumns: TableColumnsType = [
    { title: "Template Name", dataIndex: "templateName" },
    { title: "Created At", dataIndex: "createdAt" },
    { title: "Updated At", dataIndex: "modifiedAt" },
    { title: "Updated By", dataIndex: "updateBy" },
    {
      title: "Action",
      dataIndex: " ",
      width: 90,
      render: (value: any, record: any) => {
        const menuItems = [
          {
            label: (
              <Typography.Text
                type="secondary"
                style={{ color: styles?.primary }}
              >
                <Space>
                  <Icon icon="bx:edit" inline fontSize={16} style={{ color: styles?.primary }} />
                  Edit
                </Space>
              </Typography.Text>
            ),
            key: "edit",
          },
        ];
        return (
          <Dropdown
            overlay={
              <Menu
                /* The menu's own click closes it; a portalled menu still bubbles
                   up the React tree, so stop it before the row's navigate. */
                onClick={({ key, domEvent }) => {
                  domEvent.stopPropagation();
                  menuClick(key, record);
                }}
                items={menuItems}
              />
            }
            trigger={["click"]}
            placement="bottomLeft"
          >
            <span onClick={(e) => e.stopPropagation()}>
              <Icon icon={"bi:three-dots-vertical"} style={{ cursor: "pointer" }} fontSize={20} />
            </span>
          </Dropdown>
        );
      },
    },
  ];

  return (
    <>
      {loading ? (
        <div style={{ textAlign: "center", padding: "50px 0" }}>
          <Spin size="large" />
        </div>
      ) : error ? (
        <Typography.Text type="danger">{error}</Typography.Text>
      ) : (
        <>
          <DataTable
            searchPlaceholderText="Search by Template Name"
            searchQuery={searchString}
            hideExport={true}
            showTopBar={false}
            inputDisable={loading}
            totalRecords={totalRecords}
            recordsPerPage={recordsPerPage}
            clearStringSearch={clearSearch}
            onSearchChange={handleSearchChange}
            multiSelect={false}
            isPagination={true}
            isServerSearchApi={true}
            columns={templateColumns}
            tableData={templateData}
            className="table-clickable"
            onClick={(record: any) => navigate(`/workorder-manage-catalog/template-view/${record?.id}`)}
          />
          {/* Ten a page from the server, as the other catalog lists: the list
              asked for page 0 only and had no pager, so the 11th template on
              never showed. */}
          <div className="pagination">
            <Pagination
              current={page + 1}
              total={totalRecords}
              pageSize={recordsPerPage}
              showSizeChanger={false}
              onChange={(next) => setPage(next - 1)}
            />
          </div>
        </>
      )}
    </>
  );
};
